import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { AppModal, AppRemixIcon } from "@/layout/AppComponents";
import ReactDOMServer from "react-dom/server";
import NotificationAlert from "../../../../../components/helpers/notificationAlert";
import AppSpinner from "../../../../../components/AppSpinner";
import minions from "../../../../../../minions/minions";
import CustomStateIcon from "../../../../../components/helpers/CustomStateIcon";
import { axiosPath } from "../../../../../../routing/helpers/router";
import * as currentPageAction from "../../../../../../store/actions/currentPageAction";
import Translator from "../../../../../components/helpers/Translator";
import _ from "lodash";

const StateSelect01 = (props) => {
  const { plantObligationID, className } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const [showModalAssistantEvaluation, setShowModalAssistantEvaluation] =
    useState(false);
  const permissions = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.permissions
  );
  const userBridgeSettings = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.userBridge?.permissions
  );
  const state = useSelector(
    (s) =>
      s.currentPage?.plantObligationLevels?.level1?.[plantObligationID]?.state
  );
  const customState = useSelector(
    (s) =>
      s.currentPage?.plantObligationLevels?.level1?.[plantObligationID]
        ?.customState
  );
  const obsolete = useSelector(
    (s) =>
      s.currentPage?.plantObligationLevels?.level1?.[plantObligationID]
        ?.obsolete
  );
  const poLevel1 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level1
  );
  const poLevel2 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level2
  );
  const poLevel3 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level3
  );
  const stageGates = useSelector((s) => s.currentPage?.options?.stageGates);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // > CHANGE STATE
  const changeState = useCallback(
    async (newState, cs = null) => {
      setOpen(false);
      setLoading(true);
      try {
        const response = await axios.post(
          axiosPath("api_r_user_module_legalCompliance_evaluation_changeState"),
          {
            moduleBridgeHex,
            plantHex,
            plantObligation: plantObligationID,
            newState,
            newCustomState: cs,
          }
        );
        const updatedPoLevel1 = { ...poLevel1 };
        if (
          poLevel2?.[plantObligationID]?.obligation?.applicableDate &&
          poLevel2?.[plantObligationID]?.obligation?.obligationVersion
            ?.hasPreviousVersion === undefined &&
          response.data.state?.state === 0
        ) {
          updatedPoLevel1[plantObligationID] = {
            ...updatedPoLevel1[plantObligationID],
            ...{ state: -1, customState: null },
          };
        } else {
          updatedPoLevel1[plantObligationID] = {
            ...updatedPoLevel1[plantObligationID],
            ...response.data.state,
          };
        }
        if (response.data.poChangeStates && poLevel3) {
          _.map(response.data.poChangeStates, (v, k) => {
            updatedPoLevel1[k] = {
              ...updatedPoLevel1[plantObligationID],
              ...v,
            };
          });
        }
        dispatch(
          currentPageAction.change("plantObligationLevels", {
            level1: {
              ...poLevel1,
              ...updatedPoLevel1,
            },
          })
        );
        if (response.data.goalState && poLevel3) {
          dispatch(
            currentPageAction.change("plantObligationLevels", {
              level3: {
                ...poLevel3,
                ...{
                  [plantObligationID]: {
                    ...poLevel3[plantObligationID],
                    ...response.data.goalState,
                  },
                },
              },
            })
          );
        }
        setLoading(false);
        return true;
      } catch (err) {
        setLoading(false);
        NotificationAlert({
          title: "Error",
          message: "Se ha producido un error al cambiar el estado",
          type: "danger",
          position: "top-center",
          duration: 0,
        });
      }
    },
    [
      moduleBridgeHex,
      plantHex,
      plantObligationID,
      currentPageAction,
      poLevel1,
      poLevel3,
    ]
  );
  // < CHANGE STATE

  const isState1Disabled = useCallback(() => {
    if (permissions?.gcl?.module?.plant?.states?.needEvidenceToFulfilled) {
      if (
        !poLevel3?.[plantObligationID]?.alerts?.hasEvidencesWithFiles &&
        permissions?.gcl?.module?.plant?.states?.needEvidenceToFulfilled?.assign?.oIDs
          ?.split(",")
          .includes(poLevel2?.[plantObligationID]?.obligation?.id.toString())
      ) {
        return true;
      }
      if (
        (!permissions?.gcl?.module?.plant?.states?.needEvidenceToFulfilled
          ?.assign?.oIDs ||
          permissions?.gcl?.module?.plant?.states?.needEvidenceToFulfilled?.assign?.oIDs?.split(
            ","
          ).length === 0) &&
        (!poLevel3?.[plantObligationID]?.alerts?.hasEvidences ||
          (permissions?.gcl?.module?.plant?.states?.needEvidenceToFulfilled
            ?.needFile &&
            !poLevel3?.[plantObligationID]?.alerts?.hasEvidencesWithFiles))
      ) {
        return true;
      }
    }

    return false;
  }, [permissions, poLevel3, plantObligationID]);

  const handleStageGate = useCallback(() => {
    if (!stageGates) return true;
    if (stageGates?.options?.[plantHex]?.isProjectClosed) return false;

    if (stageGates?.groups) {
      for (
        let groupIndex = 0;
        groupIndex < stageGates?.groups.length;
        groupIndex++
      ) {
        if (
          stageGates?.groups?.[groupIndex]?.poIDs?.includes(
            plantObligationID
          ) &&
          stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.state
        ) {
          return false;
        }
      }
    }

    return true;
  }, [stageGates, plantHex]);

  if (state !== undefined || customState !== undefined) {
    return (
      <div
        className={`relative ${
          poLevel3 &&
          obsolete === false &&
          !poLevel3?.[plantObligationID]?.alerts?.corporateObligation &&
          (userBridgeSettings?.level !== 2 ||
            userBridgeSettings?.writeState === true) &&
          (permissions?.gcl?.module?.api?.send?.noBlock ||
            !poLevel3?.[plantObligationID]?.alerts?.apiLock) &&
          !poLevel3?.[plantObligationID]?.alerts
            ?.masterHierarchicalObligation &&
          handleStageGate()
            ? "pointer"
            : "not-allowed"
        } ${className || ""}`}
        style={{
          background: obsolete
            ? minions.obligation.stateColor(5)
            : minions.obligation.stateColor(
                state,
                customState && auth.customStates[customState]
              ),
        }}
        onClick={() =>
          poLevel3 &&
          obsolete === false &&
          (userBridgeSettings?.level !== 2 ||
            userBridgeSettings?.writeState === true) &&
          (permissions?.gcl?.module?.api?.send?.noBlock ||
            !poLevel3?.[plantObligationID]?.alerts?.apiLock) &&
          !poLevel3?.[plantObligationID]?.alerts?.corporateObligation &&
          !poLevel3?.[plantObligationID]?.alerts
            ?.masterHierarchicalObligation &&
          handleStageGate()
            ? setOpen(!open)
            : undefined
        }
      >
        {!loading ? (
          obsolete ? (
            minions.obligation.stateIcon(5)
          ) : (
            minions.obligation.stateIcon(
              state,
              customState && auth.customStates[customState]
            )
          )
        ) : (
          <AppSpinner>
            <AppRemixIcon icon="loader-4" size={30} color="white" />
          </AppSpinner>
        )}
        <StateSelectExpand className={`${open ? "expanded" : ""}`}>
          {/* <StateSelectExpandCurrentState $background={minions.obligation.stateColor(state, customState && auth.customStates[customState])}>{minions.obligation.stateIcon(state, customState && auth.customStates[customState])}</StateSelectExpandCurrentState> */}
          <StateSelectExpandContainer>
            {(!permissions.gcl?.module?.plant?.states?.unavailableStates ||
              !permissions.gcl.module.plant.states.unavailableStates.includes(
                1
              )) && (
              <StateSelectOption
                onClick={() =>
                  isState1Disabled() ? undefined : changeState(1)
                }
                $background={minions.obligation.stateColor(1)}
                $color="white"
                $isDisabled={isState1Disabled()}
              >
                <span>{minions.obligation.stateIcon(1)}</span>{" "}
                <b>
                  <Translator file="user" name="user.obligations.state1" />
                </b>
              </StateSelectOption>
            )}
            {(!permissions.gcl?.module?.plant?.states?.unavailableStates ||
              !permissions.gcl.module.plant.states.unavailableStates.includes(
                2
              )) && (
              <StateSelectOption
                $background={minions.obligation.stateColor(2)}
                $color="white"
                onClick={() => changeState(2)}
              >
                <span>{minions.obligation.stateIcon(2)}</span>{" "}
                <b>
                  <Translator file="user" name="user.obligations.state2" />
                </b>
              </StateSelectOption>
            )}
            {(!permissions.gcl?.module?.plant?.states?.unavailableStates ||
              !permissions.gcl.module.plant.states.unavailableStates.includes(
                3
              )) && (
              <StateSelectOption
                $background={minions.obligation.stateColor(3)}
                $color="white"
                onClick={() => changeState(3)}
              >
                <span>{minions.obligation.stateIcon(3)}</span>{" "}
                <b>
                  <Translator file="user" name="user.obligations.state3" />
                </b>
              </StateSelectOption>
            )}
            {(!permissions.gcl?.module?.plant?.states?.unavailableStates ||
              !permissions.gcl.module.plant.states.unavailableStates.includes(
                4
              )) && (
              <StateSelectOption
                $background={minions.obligation.stateColor(4)}
                $color="white"
                onClick={() => changeState(4)}
              >
                <span>{minions.obligation.stateIcon(4)}</span>{" "}
                <b>
                  <Translator file="user" name="user.obligations.state4" />
                </b>
              </StateSelectOption>
            )}
            {permissions.gcl?.module?.plant?.states?.customStates &&
              permissions.gcl.module.plant.states.customStates.map(
                (csID, i) => (
                  <StateSelectOption
                    key={i}
                    $background={auth.customStates[csID].bgColor}
                    $color={auth.customStates[csID].fgColor}
                    onClick={() => changeState(6, csID)}
                  >
                    <span>
                      <CustomStateIcon
                        icon={auth.customStates[csID].icon}
                        color={auth.customStates[csID].fgColor}
                        type={auth.customStates[csID].iconType}
                      />
                      {/* <AppRemixIcon icon={auth.customStates[csID].icon} color={auth.customStates[csID].fgColor} /> */}{" "}
                    </span>{" "}
                    <b>{auth.customStates[csID].name}</b>
                  </StateSelectOption>
                )
              )}
          </StateSelectExpandContainer>
          <StateSelectClose
            onClick={() => setOpen(false)}
            title={ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="close" />
            )}
          >
            <AppRemixIcon icon="close" size={23} />
          </StateSelectClose>
          {poLevel3 &&
            poLevel3[plantObligationID]?.obligationAssistantEvaluation && (
              <>
                <AssistantEvaluation
                  onClick={() => setShowModalAssistantEvaluation(true)}
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="gcl-information"
                    />
                  )}
                >
                  <AppRemixIcon
                    filled
                    icon="information"
                    size={23}
                    color="#00FF00"
                  />
                </AssistantEvaluation>
                <AppModal
                  size="lg"
                  show={showModalAssistantEvaluation}
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="legalCompliance-information.evaluation"
                    />
                  )}
                  onClose={() => setShowModalAssistantEvaluation(false)}
                >
                  <div
                    style={{ maxHeight: "450px", overflowY: "scroll" }}
                    dangerouslySetInnerHTML={{
                      __html:
                        poLevel3[plantObligationID]
                          ?.obligationAssistantEvaluation,
                    }}
                  />
                </AppModal>
              </>
            )}
          <StateSelectRemove
            onClick={() => changeState(0)}
            title={ReactDOMServer.renderToString(
              <Translator file="user" name="user.obligations.state0" />
            )}
          >
            <AppRemixIcon icon="file" size={23} />
          </StateSelectRemove>
        </StateSelectExpand>
      </div>
    );
  }
  return <div />;
};

export default StateSelect01;

const StateSelectExpand = styled.div`
  width: 0;
  overflow: hidden;
  position: absolute;
  top: 3px;
  left: 42px;
  height: 154px;
  z-index: 10;
  transition: width ease 0.4s;
  font-size: 14px;
  // border-right-width: 4px;
  // border-right-style: solid;
  display: grid;
  grid-template-columns: 40px 1fr;
  background: white;
  &.expanded {
    width: calc(100% - 43px);
  }
`;
const StateSelectExpandContainer = styled.div`
  position: relative;
  display: grid;
  grid-gap: 0;
  grid-template-columns: repeat(4, 400px);
  grid-template-rows: repeat(4, 25%);
  grid-auto-flow: column;
  width: calc(100% - 3px);
  flex-wrap: wrap;
  height: 154px;
  background: white;
`;
const StateSelectExpandCurrentState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  padding: 0;
  background: ${(props) => props.$background};
  height: 154px;
`;
const StateSelectOption = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr;
  cursor: pointer;
  align-items: center;
  color: ${(props) => props.$color};
  background: ${(props) => props.$background};
  cursor: ${(props) => (props.$isDisabled ? "not-allowed" : "pointer")};
  span {
    display: inline-block;
    //width: 36px;
    //line-height: 40px;
    font-size: 24px;
    text-align: center;
    //i{line-height: 40px;}
  }
`;

const StateSelectClose = styled.div`
  position: absolute;
  top: 5px;
  right: 10px;
`;

const StateSelectRemove = styled.div`
  position: absolute;
  bottom: 5px;
  right: 10px;
`;

const AssistantEvaluation = styled.div`
  position: absolute;
  bottom: 35px;
  right: 10px;
`;
